import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Menu,
  Settings,
  LogOut,
  Shield,
  UserPlus,
} from 'lucide-react';
import { LanguageSwitcher, LocalizedLink, useLocalizedNavigate } from '@/components/i18n';
import { cn } from '@/lib/utils';
import { useAttorneySupervision } from '@/hooks/usePrivilege';
import { useBranding } from '@/contexts/BrandingContext';
import { Link, useLocation } from 'react-router-dom';
import { stripLocaleFromPath } from '@/lib/i18n';
import { NotificationBell } from '@/components/NotificationBell';
import { ContainerStatusIndicator } from '@/components/ContainerStatusIndicator';

interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export function Header({ onMenuClick, showMenuButton = false }: HeaderProps) {
  const { user, profile, signOut } = useAuth();
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation('navigation');
  const { data: supervision, isLoading: supervisionLoading } = useAttorneySupervision();
  const { branding, isWhiteLabeled } = useBranding();

  // Only show attorney status for non-advisor users
  const isAdvisor = profile?.is_advisor === true;
  const showAttorneyStatus = !isAdvisor && !supervisionLoading;

  // View toggle logic
  const location = useLocation();
  const cleanPathname = stripLocaleFromPath(location.pathname);
  const isAdmin = profile?.role === 'admin';

  // View toggle is ADMIN-ONLY — only platform owners can switch views.
  // Regular advisors and clients never see this toggle.
  const segments: { label: string; key: string; route: string }[] = [];
  if (isAdmin) {
    segments.push({ label: 'My Case', key: 'app', route: '/dashboard' });
    if (isAdvisor) {
      // Go directly to own case in advisor view (not empty advisor dashboard)
      segments.push({ label: 'Advisor', key: 'advisor', route: `/advisor/case/${user?.id}` });
    }
    segments.push({ label: 'Admin', key: 'admin', route: '/admin' });
  }

  // Determine active segment from current route
  const activeSegment = cleanPathname.startsWith('/admin')
    ? 'admin'
    : cleanPathname.startsWith('/advisor')
      ? 'advisor'
      : 'app';

  const showViewToggle = segments.length > 1;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Get user initials for avatar
  const getInitials = () => {
    if (profile?.full_name) {
      const parts = profile.full_name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return parts[0][0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const getDisplayName = () => {
    if (profile?.full_name) {
      return profile.full_name;
    }
    return user?.email || 'User';
  };

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6">
      {/* Left side */}
      <div className="flex items-center gap-4">
        {showMenuButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="lg:hidden min-w-[44px] min-h-[44px] w-11 h-11"
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}
        {isWhiteLabeled && branding.company_name !== 'SettleWise' && (
          <span className="text-sm font-medium text-slate-600 hidden lg:block">
            {branding.company_name}
          </span>
        )}

        {/* View Toggle (Admin only) */}
        {showViewToggle && (
          <div className="flex items-center gap-1.5">
            <div className="flex items-center h-7 bg-slate-100 rounded-md p-0.5 gap-0.5">
              {segments.map((seg) => (
                <button
                  key={seg.key}
                  onClick={() => {
                    if (activeSegment !== seg.key) navigate(seg.route);
                  }}
                  className={cn(
                    'px-2.5 h-6 rounded text-xs font-medium transition-colors whitespace-nowrap',
                    activeSegment === seg.key
                      ? 'bg-navy text-cream shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'
                  )}
                >
                  {seg.label}
                </button>
              ))}
            </div>
            {/* Role selector — only visible in advisor mode */}
            {activeSegment === 'advisor' && (
              <select
                className="h-7 text-[11px] bg-white border border-slate-200 rounded-md px-1.5 text-slate-600 cursor-pointer focus:outline-none focus:ring-1 focus:ring-violet-300"
                defaultValue="lead_attorney"
                onChange={(e) => {
                  // Update the advisor_connection role in real time
                  const newRole = e.target.value;
                  import('@/lib/supabase/client').then(({ supabase }) => {
                    supabase
                      .from('advisor_connections')
                      .update({ role: newRole })
                      .eq('advisor_user_id', user?.id ?? '')
                      .eq('client_user_id', user?.id ?? '')
                      .then(() => {
                        // Force reload to pick up new permissions
                        window.location.reload();
                      });
                  });
                }}
              >
                <option value="lead_attorney">Lead Attorney</option>
                <option value="associate_attorney">Associate</option>
                <option value="paralegal">Paralegal</option>
                <option value="cpa">CPA</option>
                <option value="financial_advisor">Financial Advisor</option>
                <option value="mediator">Mediator</option>
                <option value="expert_witness">Expert Witness</option>
              </select>
            )}
          </div>
        )}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* Attorney Status Indicator */}
        {showAttorneyStatus && (
          supervision ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium cursor-default">
                  <Shield className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Protected</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">Work directed by your attorney</p>
                <p className="text-xs text-slate-400">
                  Supervised by {supervision.attorney?.profiles?.full_name || 'Your Attorney'}
                </p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/my-team"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium hover:bg-amber-100 hover:text-amber-700 transition-colors"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Add Attorney</span>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">No Attorney Connected</p>
                <p className="text-xs text-slate-400">
                  Click to connect with an attorney for privilege protection
                </p>
              </TooltipContent>
            </Tooltip>
          )
        )}

        {/* Container Boot Status */}
        <ContainerStatusIndicator />

        {/* Notification Bell */}
        <NotificationBell />

        {/* Language Switcher */}
        <LanguageSwitcher compact className="text-slate-500" />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 px-2 hover:bg-slate-100"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[150px] truncate">
                {getDisplayName()}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="font-medium">{getDisplayName()}</span>
                <span className="text-xs text-slate-500 font-normal truncate">
                  {user?.email}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <LocalizedLink to="/settings" className="cursor-pointer">
                <Settings className="h-4 w-4 mr-2" />
                {t('header.settings')}
              </LocalizedLink>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleSignOut}
              className="cursor-pointer text-red-600 focus:text-red-600"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('header.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default Header;
